/**
 * Sentry — crash reporting and error tracking.
 *
 * Initialize early in the root layout.
 * DSN is loaded from app config extra (set via SENTRY_DSN env var).
 */

import * as Sentry from '@sentry/react-native';
import Constants from 'expo-constants';

const DSN = Constants.expoConfig?.extra?.sentryDsn || '';

let _initialized = false;

/**
 * Initialize Sentry. Call once from root layout.
 * No-op if DSN is not configured.
 */
export function initSentry(): void {
  if (_initialized || !DSN) return;
  _initialized = true;

  Sentry.init({
    dsn: DSN,
    debug: __DEV__,
    enabled: !__DEV__,
    tracesSampleRate: 0.2,
    attachScreenshot: true,
    enableAutoSessionTracking: true,
  });
}

/**
 * Set user context for Sentry reports.
 */
export function setSentryUser(waId: string, lang: string, tz: string): void {
  if (!_initialized) return;
  Sentry.setUser({ id: waId });
  Sentry.setTag('language', lang);
  Sentry.setTag('timezone', tz);
  Sentry.setTag('appVersion', Constants.expoConfig?.version || 'unknown');
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser(): void {
  if (!_initialized) return;
  Sentry.setUser(null);
}

/**
 * Add a breadcrumb for navigation/API/WS events.
 */
export function addBreadcrumb(
  category: 'navigation' | 'api' | 'websocket' | 'user',
  message: string,
  data?: Record<string, string | number | boolean>,
): void {
  if (!_initialized) return;
  Sentry.addBreadcrumb({ category, message, data, level: 'info' });
}

/**
 * Capture an exception manually.
 */
export function captureException(error: unknown, context?: Record<string, string>): void {
  if (!_initialized) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export { Sentry };
